/**
 * JointIndividualGates — joint-ownership overlay for Individual carts.
 *
 * Ported from SPFx ProceduralGates._validateRelationships (Joint branch):
 *   - JOINT_MIN_OWNERS: joint accounts require 2+ owners
 *   - Required fields for each joint applicant
 *
 * Invoked in addition to IndividualGates when `cart.isJoint === true`.
 */

import type { CartSnapshot, GateResult } from '../workflow-engine/types';
import { buildRequiredFieldGates, countOwners, getByRole, type FieldSpec } from '../ProceduralGates';

import { REQUIRED_INDIVIDUAL_FIELDS } from './requiredIndividualFields';

// Single source of truth (Bill 2026-07-23) — same spec as the primary + the
// person-modal save gate. Joint owners are full owners: the server commit
// validation requires the identical field set for every non-cifId person, so
// the old 4-field subset (name/SSN/DOB, no address) under-gated here.
const JOINT_FIELDS: ReadonlyArray<FieldSpec> =
  REQUIRED_INDIVIDUAL_FIELDS.map((f) => ({ field: f.field, label: f.label }));

export function runJointIndividualGates(cart: CartSnapshot): GateResult[] {
  const gates: GateResult[] = [];

  if (countOwners(cart) < 2) {
    gates.push({
      gateCode: 'JOINT_MIN_OWNERS',
      entityType: cart.entityType ?? '',
      category: 'relationship',
      status: 'fail',
      label: 'Joint accounts require 2+ owners',
      reason: 'Joint accounts require at least 2 owners (primary + joint).',
      remediationAction: { label: 'Add joint owner', deepLinkStepId: 'people' },
    });
  }

  // Validate required fields for each joint applicant
  for (const joint of getByRole(cart, 'joint')) {
    const fieldGates = buildRequiredFieldGates(cart, joint, JOINT_FIELDS);
    for (const g of fieldGates) {
      g.gateCode = `JOINT_${g.gateCode}`;
      g.reason = `Joint owner · ${g.reason ?? ''}`.trim();
      gates.push(g);
    }
  }

  // Joint tenant agreement — v3 placeholder for signature-card joint addendum.
  gates.push({
    gateCode: 'JOINT_TENANT_AGREEMENT',
    entityType: cart.entityType ?? '',
    category: 'documentCollection',
    status: 'warn',
    label: 'Joint tenancy agreement signed',
    reason: 'Pending: JTWROS addendum captured via SignNow package at Sign step.',
  });

  return gates;
}
